import React from 'react';
import { Autocomplete, TextField, Checkbox, Divider } from '@mui/material';
import { useFirm } from 'hooks/data/firm/useFirm';

interface Props {
  value: Array<string>;
  onChange: (firmIds: Array<string>) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

type FirmOption = {
  _id: string;
  firmName: string;
};

const MultiFirmSelector: React.FC<Props> = ({
  value,
  onChange,
  error,
  helperText,
  disabled,
}) => {
  const { firms } = useFirm({ params: {} });
  const firmOptions = firms.data?.result ?? [];
  const selectedIds = value || [];
  const allSelected =
    firmOptions.length > 0 && selectedIds.length === firmOptions.length;

  const allOption: FirmOption = { _id: 'all', firmName: 'Select all' };
  const optionsWithAll = [allOption, ...firmOptions];

  const selectedOptions = allSelected
    ? [allOption, ...firmOptions.filter((f) => selectedIds.includes(f._id))]
    : firmOptions.filter((f) => selectedIds.includes(f._id));

  const handleChange = (
    _event: unknown,
    newValue: Array<FirmOption>,
    _reason: unknown,
    details?: { option: FirmOption },
  ) => {
    const clicked = details?.option;
    const others = newValue.filter((opt) => opt._id !== 'all');

    let newIds: Array<string>;

    if (clicked?._id === 'all') {
      newIds = allSelected ? [] : firmOptions.map((f) => f._id);
    } else {
      newIds = others.map((f) => f._id);
    }

    onChange(newIds);
  };

  const displayValue = selectedIds
    .map((id) => {
      const firm = firmOptions.find((f) => f._id === id);
      return firm ? firm.firmName : '';
    })
    .filter(Boolean)
    .join(', ');

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      loading={firms.isLoading}
      disabled={disabled || firms.isLoading || firmOptions.length === 0}
      options={optionsWithAll}
      getOptionLabel={(option) => option.firmName}
      getOptionKey={(option) => option._id}
      isOptionEqualToValue={(option, val) => option._id === val._id}
      value={selectedOptions}
      onChange={handleChange}
      renderValue={() => null}
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props;
        return (
          <li key={key} {...optionProps} aria-selected={selected}>
            <Checkbox checked={selected} tabIndex={-1} disableRipple />
            {option.firmName}
            {option._id === 'all' && <Divider />}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          placeholder="Select firm(s)"
          error={error}
          helperText={helperText}
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              value:
                typeof params.inputProps.value === 'string' &&
                params.inputProps.value.length > 0
                  ? params.inputProps.value
                  : displayValue,
            },
          }}
        />
      )}
    />
  );
};

export default MultiFirmSelector;
